// src/module_pharmacy/LedgerView.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faTimes, faSpinner, faStore } from '@fortawesome/free-solid-svg-icons';

const API_BASE = 'https://ourzestapp.online/oz_backend/mechanics_module';
const TRANSACTIONS_API = `${API_BASE}/cosmetics_transactions.php`;

const LedgerView = () => {
    const [loading, setLoading] = useState(true);
    const [allTransactions, setAllTransactions] = useState([]);
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [businessInfo, setBusinessInfo] = useState({});
    const [shopInfo, setShopInfo] = useState({});
    const [filters, setFilters] = useState({});
    const [summary, setSummary] = useState({});

    const urlParams = new URLSearchParams(window.location.search);
    const shopId = urlParams.get('shop_id');
    const username = urlParams.get('username');

    // ── Format Helpers ──
    const formatCurrency = (amount) => {
        if (amount === undefined || amount === null) return '0.00';
        return parseFloat(amount).toFixed(2);
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatFooterDate = (date) => {
        const day = date.getDate();
        const month = date.toLocaleString('en-US', { month: 'long' });
        const year = date.getFullYear();
        let hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const ampm = hours >= 12 ? 'pm' : 'am';
        hours = hours % 12;
        if (hours === 0) hours = 12;
        return `${day} ${month} ${year} at ${hours}:${minutes} ${ampm}`;
    };

    const getTransactionTypeLabel = (type) => {
        const map = {
            sale: 'Sale',
            due: 'Customer Due',
            payment_received: 'Customer Payment',
            payment_to_supplier: 'Supplier Payment',
            supplier_due: 'Supplier Due',
            salary_payment: 'Salary Payment',
            expense: 'Shop Expense',
            return: 'Return',
            purchase: 'Purchase'
        };
        return map[type] || type;
    };

    const getTransactionTypeClass = (type) => {
        const map = {
            sale: 'type-sale',
            due: 'type-due',
            payment_received: 'type-payment-received',
            payment_to_supplier: 'type-payment-to-supplier',
            supplier_due: 'type-supplier-due',
            salary_payment: 'type-salary',
            expense: 'type-expense',
            return: 'type-return',
            purchase: 'type-purchase'
        };
        return map[type] || 'type-default';
    };

    const getStatusClass = (status) => {
        if (status === 'completed' || status === 'paid') return 'success';
        if (status === 'pending') return 'pending';
        if (status === 'cancelled') return 'cancelled';
        return 'default';
    };

    const getStatusLabel = (status) => {
        if (status === 'completed' || status === 'paid') return 'Paid';
        if (status === 'pending') return 'Pending';
        if (status === 'cancelled') return 'Cancelled';
        return status || 'Completed';
    };

    // ── Load Data ──
    const loadLedgerData = useCallback(async () => {
    if (!shopId) {
        setLoading(false);
        return;
    }

    setLoading(true);
    try {
        const startDate = urlParams.get('start_date') || '';
        const endDate = urlParams.get('end_date') || '';
        const transactionType = urlParams.get('transaction_type') || '';
        const status = urlParams.get('status') || '';
        const search = urlParams.get('search') || '';
        const tab = urlParams.get('tab') || 'all';

        setFilters({ startDate, endDate, transactionType, status, search, tab });

        const body = {
            action: 'get_transactions',
            shop_id: shopId,
            limit: 10000,
            offset: 0
        };

        const tabTypeMap = {
            sales: 'sale',
            salary: 'salary_payment',
            expense: 'expense',
            purchases: 'purchase',
            returns: 'return'
        };
        if (tabTypeMap[tab]) {
            body.transaction_type = tabTypeMap[tab];
        } else if (transactionType) {
            body.transaction_type = transactionType;
        }

        if (startDate) body.start_date = startDate;
        if (endDate) body.end_date = endDate;
        if (status) body.status = status;

        const res = await fetch(TRANSACTIONS_API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });

        const data = await res.json();

        if (data.status === 'success') {
            const all = data.data || [];
            
            // Sort transactions by date and ID
            const sortedTransactions = [...all].sort((a, b) => {
                const d1 = new Date(a.created_at || a.transaction_date || 0);
                const d2 = new Date(b.created_at || b.transaction_date || 0);
                if (d1.getTime() !== d2.getTime()) {
                    return d1 - d2;
                }
                return (parseInt(a.id) || 0) - (parseInt(b.id) || 0);
            });

            // Process transactions with cascading balances (same logic as main component)
            const processedTransactions = processCascadingBalances(sortedTransactions);
            
            setAllTransactions(processedTransactions);
            
            // Calculate totals
            let totalAmount = 0;
            let totalPaid = 0;
            let totalBalance = 0;
            
            processedTransactions.forEach(tx => {
                const amount = parseFloat(tx.grand_total || tx.amount || 0);
                const paid = parseFloat(tx.amount_paid || tx.paid_amount || 0);
                const balance = parseFloat(tx.amount_due || tx.balance || 0);
                totalAmount += amount;
                totalPaid += paid;
                totalBalance += balance > 0 ? balance : 0;
            });
            
            setSummary({
                totalAmount,
                totalPaid,
                totalBalance,
                count: processedTransactions.length
            });

            // Get business info
            if (username) {
                try {
                    const bizRes = await fetch(`https://ourzestapp.online/oz_backend/root_users.php?action=get_business_info&username=${encodeURIComponent(username)}`, { credentials: 'include' });
                    const bizData = await bizRes.json();
                    if (bizData.status === 'success') {
                        setBusinessInfo(bizData);
                    }
                } catch (e) {}
            }

            const shopName = urlParams.get('shop_name') || urlParams.get('business_name') || 'Shop';
            setShopInfo({ shop_name: shopName });
        }
    } catch (err) {
        console.error('Failed to load ledger:', err);
    } finally {
        setLoading(false);
    }
}, [shopId, username, urlParams]);

    // ── Client-side filtering effect ──
    useEffect(() => {
        if (allTransactions.length === 0) {
            setFilteredTransactions([]);
            return;
        }

        const searchTerm = (filters.search || '').trim().toLowerCase();
        const startDate = filters.startDate || '';
        const endDate = filters.endDate || '';
        const transactionType = filters.transactionType || '';
        const status = filters.status || '';

        let filtered = [...allTransactions];

        if (searchTerm) {
            filtered = filtered.filter(tx => {
                const referenceName = (tx.reference_name || '').toLowerCase();
                const description = (tx.description || '').toLowerCase();
                const invoiceNumber = (tx.invoice_number || '').toLowerCase();
                const customerName = (tx.customer_name || '').toLowerCase();
                const supplierName = (tx.supplier_name || '').toLowerCase();
                const employeeName = (tx.employee_name || '').toLowerCase();
                return referenceName.includes(searchTerm) ||
                       description.includes(searchTerm) ||
                       invoiceNumber.includes(searchTerm) ||
                       customerName.includes(searchTerm) ||
                       supplierName.includes(searchTerm) ||
                       employeeName.includes(searchTerm);
            });
        }

        if (startDate) {
            filtered = filtered.filter(tx => {
                const txDate = tx.transaction_date || tx.date || '';
                return txDate >= startDate;
            });
        }
        if (endDate) {
            filtered = filtered.filter(tx => {
                const txDate = tx.transaction_date || tx.date || '';
                return txDate <= endDate;
            });
        }

        if (transactionType) {
            filtered = filtered.filter(tx => tx.transaction_type === transactionType);
        }

        if (status) {
            filtered = filtered.filter(tx => {
                const txStatus = tx.status || tx.payment_status || '';
                return txStatus === status;
            });
        }

        setFilteredTransactions(filtered);

        let totalAmount = 0;
        let totalPaid = 0;
        let totalBalance = 0;

        filtered.forEach(tx => {
            const amount = parseFloat(tx.grand_total || tx.amount || 0);
            const paid = parseFloat(tx.amount_paid || tx.paid_amount || 0);
            const balance = parseFloat(tx.amount_due || tx.balance || 0);
            totalAmount += amount;
            totalPaid += paid;
            totalBalance += balance > 0 ? balance : 0;
        });

        setSummary(prev => ({
            ...prev,
            totalAmount,
            totalPaid,
            totalBalance,
            count: filtered.length
        }));

    }, [allTransactions, filters.search, filters.startDate, filters.endDate, filters.transactionType, filters.status]);

    useEffect(() => {
        loadLedgerData();
    }, [loadLedgerData]);

    const totalAmount = summary.totalAmount || 0;
    const totalPaid = summary.totalPaid || 0;
    const totalBalance = summary.totalBalance || 0;
    const displayTransactions = filteredTransactions.length > 0 ? filteredTransactions : allTransactions;
    const businessName = businessInfo?.businessname || shopInfo?.shop_name || 'Complete Transactions Ledger';

    // ── Print ──
    const handlePrint = () => {
        window.print();
    };

    // ── Close ──
    const handleClose = () => {
        window.close();
    };

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                fontFamily: 'Arial, sans-serif',
                color: '#475569'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ color: '#10b981' }} />
                    <p style={{ marginTop: '16px' }}>Loading ledger data...</p>
                </div>
            </div>
        );
    }

    if (!shopId) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                fontFamily: 'Arial, sans-serif',
                color: '#475569'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <FontAwesomeIcon icon={faStore} size="3x" style={{ color: '#94a3b8' }} />
                    <h3 style={{ marginTop: '16px' }}>No Shop Selected</h3>
                    <p>Please select a shop first</p>
                </div>
            </div>
        );
    }

    return (
        <div className="oztransui-ledger-print-container">
            <div className="oztransui-ledger-print-header">
                <div className="oztransui-ledger-print-actions">
                    <button className="oztransui-btn-print-ledger" onClick={handlePrint}>
                        <FontAwesomeIcon icon={faPrint} /> Print / PDF
                    </button>
                    <button className="oztransui-btn-close-ledger" onClick={handleClose}>
                        <FontAwesomeIcon icon={faTimes} /> Close
                    </button>
                </div>
            </div>

            {/* ── One continuous document. On screen it just flows.
                 On print, the browser paginates the table natively across
                 as many A4 pages as it actually needs — the <thead> repeats
                 automatically on every printed page. ── */}
            <div className="oztransui-ledger-doc">

                <div className="oztransui-ledger-print-header-section">
                    <div className="oztransui-ledger-print-brand">
                        {businessInfo?.bussinesslogo_url && (
                            <div className="oztransui-ledger-print-logo-wrap">
                                <img
                                    src={businessInfo.bussinesslogo_url}
                                    alt="Logo"
                                    className="oztransui-ledger-print-logo"
                                    onError={(e) => e.target.style.display = 'none'}
                                />
                            </div>
                        )}
                        <div className="oztransui-ledger-print-title">
                            <h1>{businessName}</h1>
                            {businessInfo?.businessaddress && <p>{businessInfo.businessaddress}</p>}
                            <p className="oztransui-ledger-print-contact">
                                {businessInfo?.phonenumber && <span>📞 {businessInfo.phonenumber}</span>}
                                {businessInfo?.email && <span>✉ {businessInfo.email}</span>}
                            </p>
                        </div>
                    </div>
                    <div className="oztransui-ledger-print-meta">
                        <div className="oztransui-ledger-print-date">
                            <strong>Generated:</strong> {formatFooterDate(new Date())}
                        </div>
                        <div className="oztransui-ledger-print-filters">
                            {filters.startDate && <span className="oztransui-print-filter-tag">From: {formatDate(filters.startDate)}</span>}
                            {filters.endDate && <span className="oztransui-print-filter-tag">To: {formatDate(filters.endDate)}</span>}
                            {filters.transactionType && <span className="oztransui-print-filter-tag">Type: {getTransactionTypeLabel(filters.transactionType)}</span>}
                            {filters.status && <span className="oztransui-print-filter-tag">Status: {getStatusLabel(filters.status)}</span>}
                            {filters.search && <span className="oztransui-print-filter-tag">🔍 Search: "{filters.search}"</span>}
                        </div>
                        {filteredTransactions.length !== allTransactions.length && (
                            <div className="oztransui-ledger-print-filtered-note">
                                Showing {filteredTransactions.length} of {allTransactions.length} transactions
                            </div>
                        )}
                    </div>
                </div>

                <div className="oztransui-ledger-print-summary">
                    <div className="oztransui-print-summary-card">
                        <span className="oztransui-print-summary-label">TOTAL AMOUNT</span>
                        <span className="oztransui-print-summary-value">Rs. {formatCurrency(totalAmount)}</span>
                    </div>
                    <div className="oztransui-print-summary-card">
                        <span className="oztransui-print-summary-label">PAID</span>
                        <span className="oztransui-print-summary-value oztransui-summary-paid">Rs. {formatCurrency(totalPaid)}</span>
                    </div>
                    <div className="oztransui-print-summary-card">
                        <span className="oztransui-print-summary-label">BALANCE (DUES)</span>
                        <span className={`oztransui-print-summary-value ${totalBalance > 0 ? 'oztransui-summary-due' : 'oztransui-summary-clear'}`}>Rs. {formatCurrency(totalBalance)}</span>
                    </div>
                    <div className="oztransui-print-summary-card">
                        <span className="oztransui-print-summary-label">TRANSACTIONS</span>
                        <span className="oztransui-print-summary-value">{displayTransactions.length}</span>
                    </div>
                </div>

                <div className="oztransui-ledger-print-table-wrapper">
                    <table className="oztransui-ledger-print-table">
                        <thead>
                            <tr>
                                <th className="oztransui-col-center">#</th>
                                <th className="oztransui-col-center">Date</th>
                                <th className="oztransui-col-center">Type</th>
                                <th className="oztransui-col-left">Reference</th>
                                <th className="oztransui-col-left">Description</th>
                                <th className="oztransui-col-center">Grand Total</th>
                                <th className="oztransui-col-center">Paid</th>
                                <th className="oztransui-col-center">Balance</th>
                                <th className="oztransui-col-center">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayTransactions.length === 0 ? (
                                <tr>
                                    <td colSpan="9" className="oztransui-empty-cell">
                                        {filters.search ? (
                                            <>No transactions found matching "<strong>{filters.search}</strong>"</>
                                        ) : (
                                            'No transactions found'
                                        )}
                                    </td>
                                </tr>
                            ) : (
                                displayTransactions.map((tx, idx) => (
                                    <tr key={tx.transaction_id || idx} className="data-row">
                                        <td className="oztransui-col-center">{idx + 1}</td>
                                        <td className="oztransui-col-center">{formatDate(tx.transaction_date || tx.date)}</td>
                                        <td className="oztransui-col-center">
                                            <span className={`oztransui-print-type-badge ${getTransactionTypeClass(tx.transaction_type)}`}>
                                                {getTransactionTypeLabel(tx.transaction_type)}
                                            </span>
                                        </td>
                                        <td className="oztransui-col-left">{tx.reference_name || '-'}</td>
                                        <td className="oztransui-col-left">{tx.description || '-'}</td>
                                        <td className="oztransui-col-center oztransui-col-strong">Rs. {formatCurrency(tx.grand_total || tx.amount || 0)}</td>
                                        <td className="oztransui-col-center">Rs. {formatCurrency(tx.amount_paid || tx.paid_amount || 0)}</td>
                                        <td className={`oztransui-col-center oztransui-col-strong ${(tx.amount_due || tx.balance || 0) > 0 ? 'oztransui-text-due' : 'oztransui-text-clear'}`}>
                                            Rs. {formatCurrency(tx.amount_due || tx.balance || 0)}
                                        </td>
                                        <td className="oztransui-col-center">
                                            <span className={`oztransui-print-status-badge ${getStatusClass(tx.status || tx.payment_status)}`}>
                                                {getStatusLabel(tx.status || tx.payment_status)}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                            {displayTransactions.length > 0 && (
                                <tr className="oztransui-total-row">
                                    <td colSpan="5" className="oztransui-col-left">TOTALS</td>
                                    <td className="oztransui-col-center">Rs. {formatCurrency(totalAmount)}</td>
                                    <td className="oztransui-col-center">Rs. {formatCurrency(totalPaid)}</td>
                                    <td className={`oztransui-col-center ${totalBalance > 0 ? 'oztransui-text-due' : 'oztransui-text-clear'}`}>Rs. {formatCurrency(totalBalance)}</td>
                                    <td></td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* ── Single closing line, appears once at the true end of the document ── */}
                <div className="oztransui-doc-footer">
                    Developed by <strong>OURZEST PVT LTD.</strong>
                </div>
            </div>

            {/* ── Styles ── */}
            <style dangerouslySetInnerHTML={{ __html: `
                * { box-sizing: border-box; }

                .oztransui-ledger-print-container {
                    font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
                    color: #1f2937;
                    background: #f1f5f9;
                    min-height: 100vh;
                }

                /* ── Top action bar (screen only) ── */
                .oztransui-ledger-print-header {
                    display: flex;
                    justify-content: flex-end;
                    padding: 14px 24px;
                    background: #ffffff;
                    position: sticky;
                    top: 0;
                    z-index: 100;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
                }

                .oztransui-ledger-print-actions {
                    display: flex;
                    gap: 10px;
                }

                .oztransui-btn-print-ledger {
                    padding: 7px 15px;
                    background: #10b981;
                    color: #fff;
                    border: none;
                    font-size: 12px;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    border-radius: 0;
                    transition: background 0.15s ease;
                }

                .oztransui-btn-print-ledger:hover {
                    background: #059669;
                }

                .oztransui-btn-close-ledger {
                    padding: 7px 15px;
                    background: #f1f5f9;
                    color: #475569;
                    border: 1px solid #e2e8f0;
                    font-size: 12px;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    border-radius: 0;
                    transition: background 0.15s ease, color 0.15s ease;
                }

                .oztransui-btn-close-ledger:hover {
                    background: #fee2e2;
                    color: #dc2626;
                }

                /* ── The continuous document (screen: card look; print: flat) ── */
                .oztransui-ledger-doc {
                    max-width: 1550px;
                    margin: 10px auto;
                    background: #ffffff;
                    border-radius: 0;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 10px 30px rgba(0,0,0,0.06);
                    padding: 20px 36px 28px;
                }

                .oztransui-ledger-print-header-section {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    // padding-bottom: 22px;
                    // border-bottom: 3px solid #10b981;
                    // margin-bottom: 24px;
                    flex-wrap: wrap;
                    gap: 16px;
                }

                .oztransui-ledger-print-brand {
                    display: flex;
                    align-items: center;
                    gap: 18px;
                }

                .oztransui-ledger-print-logo-wrap {
                    width: 72px;
                    height: 72px;
                    flex-shrink: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .oztransui-ledger-print-logo {
                    max-height: 72px;
                    max-width: 72px;
                    object-fit: contain;
                }

                .oztransui-ledger-print-title {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                }

                .oztransui-ledger-print-title h1 {
                    font-size: 28px;
                    font-weight: 800;
                    color: #0f172a;
                    margin: 0 0 6px 0;
                    line-height: 1.15;
                    letter-spacing: -0.3px;
                }

                .oztransui-ledger-print-title p {
                    font-size: 14px;
                    color: #64748b;
                    margin: 3px 0;
                }

                .oztransui-ledger-print-contact {
                    display: flex;
                    gap: 18px;
                    flex-wrap: wrap;
                }

                .oztransui-ledger-print-meta {
                    text-align: right;
                    font-size: 13px;
                    color: #64748b;
                }

                .oztransui-ledger-print-date {
                    margin-bottom: 8px;
                    font-size: 14px;
                }

                .oztransui-ledger-print-filters {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 6px;
                    justify-content: flex-end;
                }

                .oztransui-print-filter-tag {
                    display: inline-block;
                    padding: 4px 12px;
                    border: 1px solid #cbd5e1;
                    border-radius: 0;
                    font-size: 12px;
                    font-weight: 600;
                    color: #475569;
                    background: #f8fafc;
                }

                .oztransui-ledger-print-filtered-note {
                    margin-top: 6px;
                    font-size: 12px;
                    color: #2563eb;
                    font-weight: 600;
                }

                .oztransui-ledger-print-summary {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 14px;
                    margin: 0 0 26px 0;
                }

                .oztransui-print-summary-card {
                    border: 1px solid #e2e8f0;
                    background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
                    border-radius: 0;
                    padding: 14px 16px;
                    text-align: center;
                }

                .oztransui-print-summary-label {
                    display: block;
                    font-size: 11px;
                    text-transform: uppercase;
                    color: #64748b;
                    font-weight: 800;
                    letter-spacing: 0.6px;
                }

                .oztransui-print-summary-value {
                    display: block;
                    font-size: 22px;
                    font-weight: 800;
                    color: #0f172a;
                    margin-top: 4px;
                    letter-spacing: 0.2px;
                }

                .oztransui-summary-paid { color: #2563eb; }
                .oztransui-summary-due { color: #d97706; }
                .oztransui-summary-clear { color: #059669; }

                .oztransui-ledger-print-table-wrapper {
                    overflow-x: auto;
                    border-radius: 0;
                    border: 1px solid #e2e8f0;
                }

                .oztransui-ledger-print-table {
                    width: 100%;
                    min-width: 760px;
                    table-layout: auto;
                    border-collapse: collapse;
                    font-size: 14px;
                }

                .oztransui-ledger-print-table th {
                    padding: 14px 14px;
                    font-weight: 800;
                    font-size: 11.5px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    color: #ffffff;
                    background: #0f172a;
                    white-space: nowrap;
                    position: sticky;
                    top: 0;
                }

                .oztransui-ledger-print-table th:first-child { border-top-left-radius: 0; }
                .oztransui-ledger-print-table th:last-child { border-top-right-radius: 0; }

                .oztransui-ledger-print-table td {
                    padding: 12px 14px;
                    border-bottom: 1px solid #eef2f6;
                    color: #1e293b;
                }

                .oztransui-ledger-print-table tbody tr.data-row:nth-child(even) {
                    background: #f8fafc;
                }

                .oztransui-col-center { text-align: center; white-space: nowrap; }
                .oztransui-col-left { text-align: left; white-space: normal; word-break: break-word; }
                .oztransui-col-strong { font-weight: 700; }

                .oztransui-text-due { color: #d97706; }
                .oztransui-text-clear { color: #059669; }

                .oztransui-print-type-badge,
                .oztransui-print-status-badge {
                    display: inline-block;
                    font-weight: 700;
                    font-size: 12px;
                    white-space: nowrap;
                    padding: 4px 12px;
                    border-radius: 0;
                }

                .oztransui-print-type-badge.type-sale { color: #059669; background: #ecfdf5; }
                .oztransui-print-type-badge.type-due { color: #d97706; background: #fffbeb; }
                .oztransui-print-type-badge.type-payment-received { color: #059669; background: #ecfdf5; }
                .oztransui-print-type-badge.type-payment-to-supplier { color: #7c3aed; background: #f5f3ff; }
                .oztransui-print-type-badge.type-supplier-due { color: #d97706; background: #fffbeb; }
                .oztransui-print-type-badge.type-salary { color: #db2777; background: #fdf2f8; }
                .oztransui-print-type-badge.type-expense { color: #dc2626; background: #fef2f2; }
                .oztransui-print-type-badge.type-return { color: #ea580c; background: #fff7ed; }
                .oztransui-print-type-badge.type-purchase { color: #4f46e5; background: #eef2ff; }
                .oztransui-print-type-badge.type-default { color: #475569; background: #f1f5f9; }

                .oztransui-print-status-badge.success { color: #059669; background: #ecfdf5; }
                .oztransui-print-status-badge.pending { color: #d97706; background: #fffbeb; }
                .oztransui-print-status-badge.cancelled { color: #dc2626; background: #fef2f2; }
                .oztransui-print-status-badge.default { color: #475569; background: #f1f5f9; }

                .oztransui-empty-cell {
                    text-align: center;
                    padding: 50px;
                    color: #94a3b8;
                    font-size: 15px;
                }

                .oztransui-total-row td {
                    font-weight: 800;
                    font-size: 15px;
                    border-top: 2px solid #0f172a;
                    border-bottom: none;
                    background: #f8fafc;
                    padding-top: 14px;
                    padding-bottom: 14px;
                }

                .oztransui-doc-footer {
                    text-align: center;
                    margin-top: 32px;
                    padding-top: 18px;
                    border-top: 1px solid #e2e8f0;
                    font-size: 13px;
                    color: #64748b;
                    letter-spacing: 0.3px;
                }

                .oztransui-doc-footer strong {
                    color: #0f172a;
                }

                /* ── PRINT: reset the "card" look, let the browser paginate the
                     table natively across as many A4 pages as needed. The
                     thead repeats on every physical page automatically. ── */
                @page {
                    size: A4;
                    margin: 12mm 10mm;
                }

                @media print {
                    html, body {
                        height: auto !important;
                        overflow: visible !important;
                        background: #ffffff !important;
                    }

                    body {
                        margin: 0 !important;
                        padding: 0 !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                        background: #ffffff !important;
                    }

                    .oztransui-ledger-print-header {
                        display: none !important;
                    }

                    .oztransui-ledger-doc {
                        max-width: 100% !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        border-radius: 0 !important;
                        box-shadow: none !important;
                    }

                    .oztransui-ledger-print-title h1 { font-size: 19px; }
                    .oztransui-ledger-print-title p { font-size: 11px; }
                    .oztransui-ledger-print-meta { font-size: 11px; }
                    .oztransui-print-summary-value { font-size: 15px; }
                    .oztransui-print-summary-label { font-size: 9px; }

                    .oztransui-ledger-print-table-wrapper {
                        border: none;
                        border-radius: 0;
                        overflow: visible;
                    }

                    .oztransui-ledger-print-table {
                        font-size: 10.5px;
                        min-width: 0;
                    }

                    .oztransui-ledger-print-table th {
                        position: static;
                        background: #ffffff !important;
                        color: #1f2937 !important;
                        border-bottom: 2px solid #1f2937;
                        padding: 6px 8px;
                        border-radius: 0 !important;
                    }

                    .oztransui-ledger-print-table td {
                        padding: 5px 8px;
                    }

                    .oztransui-ledger-print-table tbody tr.data-row:nth-child(even) {
                        background: transparent;
                    }

                    .oztransui-print-type-badge,
                    .oztransui-print-status-badge {
                        background: transparent !important;
                        padding: 0;
                        font-size: 9.5px;
                    }

                    .oztransui-print-filter-tag,
                    .oztransui-print-summary-card {
                        background: #f9fafb !important;
                        border-radius: 0;
                    }

                    /* Native pagination: repeat header row on every printed page,
                       never split a row across a page boundary. */
                    .oztransui-ledger-print-table thead {
                        display: table-header-group;
                    }

                    .oztransui-ledger-print-table tbody tr {
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }

                    .oztransui-doc-footer {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }
                }

                /* ── MOBILE: keep it big, clear and usable on small screens ── */
                @media screen and (max-width: 768px) {
                    .oztransui-ledger-print-header {
                        padding: 12px 16px;
                    }

                    .oztransui-ledger-print-actions {
                        width: 100%;
                    }

                    .oztransui-btn-print-ledger,
                    .oztransui-btn-close-ledger {
                        flex: 1;
                        justify-content: center;
                        font-size: 14px;
                        padding: 12px 14px;
                    }

                    .oztransui-ledger-doc {
                        margin: 12px;
                        padding: 20px 16px 20px;
                        border-radius: 0;
                    }

                    .oztransui-ledger-print-header-section {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .oztransui-ledger-print-title h1 {
                        font-size: 21px;
                    }

                    .oztransui-ledger-print-meta {
                        text-align: left;
                    }

                    .oztransui-ledger-print-filters {
                        justify-content: flex-start;
                    }

                    .oztransui-ledger-print-summary {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 10px;
                    }

                    .oztransui-print-summary-value {
                        font-size: 18px;
                    }

                    .oztransui-ledger-print-table {
                        font-size: 13px;
                        min-width: 680px;
                    }
                }

                @media screen and (max-width: 480px) {
                    .oztransui-ledger-print-summary {
                        grid-template-columns: 1fr 1fr;
                    }

                    .oztransui-ledger-print-title h1 {
                        font-size: 19px;
                    }

                    .oztransui-btn-print-ledger,
                    .oztransui-btn-close-ledger {
                        font-size: 13px;
                        padding: 11px 10px;
                    }
                }
            `}} />
        </div>
    );
};

export default LedgerView;